import { AuthState } from "../base/AuthState";
import template from "./LoginTemplate.html";

import { _lcs } from "../../i18n";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * LoginState — guest sign-in (white-label hero + form).
 *
 * "Anmelden" authenticates against the core-api (tenant resolved from the
 * subdomain). "Als Gast fortfahren" enters the app anonymously — the guest-app
 * is usable during a stay without an account.
 */
export class LoginState extends AuthState
{
    static ROUTE = "/login";
    static ID = "login";

    async enter()
    {
        this.renderAuth( template, {
            house   : this._houseName(),
            region  : _lcs( "Sächsische Schweiz" ),
            logoUrl : this.app.accommodation?.theme?.logoUrl || null
        } );

        this.bindPasswordToggle();
        this.bindForm();
        this.bindLinks();
    }

    bindLinks()
    {
        const guest = document.querySelector( '[data-id="guest"]' );
        this.addEventListener( guest, "click", () => this.app.enterAsGuest() );

        const toSignup = document.querySelector( '[data-id="to-signup"]' );
        this.addEventListener( toSignup, "click", ( e ) =>
        {
            e.preventDefault();
            this.app.router.redirect( "/signup" );
        } );

        const forgot = document.querySelector( '[data-id="forgot"]' );
        this.addEventListener( forgot, "click", ( e ) =>
        {
            e.preventDefault();
            this.app.router.redirect( "/password-forgotten" );
        } );
    }

    bindPasswordToggle()
    {
        const toggle = document.getElementById( "togglePw" );
        const pw = document.getElementById( "password" );
        if ( !toggle || !pw )
        {
            return;
        }
        const icon = toggle.querySelector( ".icon" );

        this.addEventListener( toggle, "click", () =>
        {
            const show = pw.type === "password";
            pw.type = show ? "text" : "password";
            toggle.setAttribute( "aria-pressed", String( show ) );
            toggle.setAttribute( "aria-label",
                show ? _lcs( "Passwort verbergen" ) : _lcs( "Passwort anzeigen" ) );
            if ( icon )
            {
                icon.classList.toggle( "icon--eye", !show );
                icon.classList.toggle( "icon--eye-off", show );
            }
            pw.focus();
        } );
    }

    bindForm()
    {
        const form = document.querySelector( ".auth__form" );
        if ( !form )
        {
            return;
        }
        const errorEl = form.querySelector( '[data-id="login-error"]' );
        const submitBtn = form.querySelector( 'button[type="submit"]' );

        this.addEventListener( form, "submit", async ( e ) =>
        {
            e.preventDefault();
            this._clearError( errorEl );

            const email = ( document.getElementById( "email" )?.value || "" ).trim();
            const password = document.getElementById( "password" )?.value || "";

            if ( !EMAIL_RE.test( email ) )
            {
                this._showError( errorEl, _lcs( "Bitte gib eine gültige E-Mail-Adresse ein." ) );
                return;
            }
            if ( !password )
            {
                this._showError( errorEl, _lcs( "Bitte gib dein Passwort ein." ) );
                return;
            }

            this._setLoading( submitBtn, true );
            try
            {
                const payload = { email, password, app: "guest" };

                // The house the guest is signing in AT. Signing in is what
                // creates their relation to it (see core-api SessionPostRequest);
                // without a resolved tenant there is simply no house to link.
                const slug = this.app.theme.getTenantSlug();
                if ( slug ) payload.slug = slug;

                const { result } = await this.app.api.post( "/auth/session", payload );

                if ( result && +result.status === 201 )
                {
                    this.app.storage.saveSessionToken( result.data.token );
                    await this.app.resolveSession();
                    this.app.router.redirect( "/" );
                    await this._remindToValidate();
                }
                else
                {
                    throw new Error( "Unexpected login response." );
                }
            }
            catch ( err )
            {
                this._setLoading( submitBtn, false );
                this._showError( errorEl, this._errorMessageForStatus( +err?.status ) );
            }
        } );
    }

    /**
     * Nudge a guest whose address is still unconfirmed.
     *
     * An unvalidated account is deliberately allowed into the guest-app — a
     * guest who has just arrived should not be locked out of the house
     * information over an unread mail. But the reminder has to be acknowledged
     * rather than fade away, which is why it is a notice and not a toast.
     *
     * Shown after the redirect, so it lands on the home screen the guest is
     * about to read rather than on the login form they are leaving.
     */
    async _remindToValidate()
    {
        if ( this.app.user?.isValidated !== false )
        {
            return;
        }

        await this.app.view.notice( {
            icon : "mail",
            title : _lcs( "E-Mail noch nicht bestätigt" ),
            text : _lcs( "Wir haben dir einen Bestätigungslink geschickt. Öffne ihn, damit dein " +
                         "Konto vollständig aktiv ist. Bis dahin kannst du die App schon nutzen." ),
            confirmLabel : _lcs( "Alles klar" )
        } );
    }

    _houseName()
    {
        const slug = this.app.theme.getTenantSlug();
        if ( !slug )
        {
            return "Gästehaus Felsenblick";
        }
        return slug
            .split( "-" )
            .map( ( w ) => w.charAt( 0 ).toUpperCase() + w.slice( 1 ) )
            .join( " " );
    }

    _errorMessageForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Bitte überprüfe deine Eingaben." );
            case 401:
            case 403: return _lcs( "Zugang verweigert. Prüfe deine Zugangsdaten." );
            case 404: return _lcs( "Kein Konto mit dieser E-Mail-Adresse gefunden." );
            default:  return _lcs( "Anmeldung fehlgeschlagen. Du kannst auch als Gast fortfahren." );
        }
    }

    _setLoading( btn, on )
    {
        if ( !btn ) return;
        btn.disabled = on;
        btn.setAttribute( "aria-busy", String( on ) );
    }

    _showError( el, message )
    {
        if ( !el ) return;
        el.textContent = message;
        el.hidden = false;
    }

    _clearError( el )
    {
        if ( !el ) return;
        el.textContent = "";
        el.hidden = true;
    }

    async exit() {}
}
